import { useId, useState } from "react"

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Checkbox,
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Slider,
  Textarea,
} from "@coval/ui"
import { Eye, EyeOff } from "lucide-react"

import {
  getApiKeyPrefixError,
  getTimeoutSecondsError,
  parseTimeoutSecondsInput,
} from "@/utils/simulation/realtimeFieldValidation"
import {
  GEMINI_REALTIME_DEFAULT_MODEL,
  GEMINI_REALTIME_DEFAULT_VOICE,
  GEMINI_REALTIME_MODELS,
  GEMINI_REALTIME_VOICES,
  GROK_REALTIME_DEFAULT_MODEL,
  GROK_REALTIME_DEFAULT_VOICE,
  GROK_REALTIME_MODELS,
  GROK_REALTIME_VOICES,
  isOpenAIRealtimeApiKeySource,
  OPENAI_GPT_LIVE_ALPHA_MODELS,
  OPENAI_GPT_LIVE_ALPHA_ONLY_VOICE_VALUES,
  OPENAI_REALTIME_API_KEY_SOURCE,
  OPENAI_REALTIME_DEFAULT_MODEL,
  OPENAI_REALTIME_DEFAULT_VOICE,
  OPENAI_REALTIME_MODELS,
  OPENAI_REALTIME_VOICES,
  type OpenAIRealtimeApiKeySource,
} from "@/utils/simulation/simulatorFields"

interface BaseFormFieldProps {
  disabled?: boolean
  className?: string
}

interface GeminiRealtimeFieldsProps extends BaseFormFieldProps {
  apiKey: string
  instructions?: string
  model?: string
  voice?: string
  temperature?: number
  timeoutSeconds?: number
  onApiKeyChange: (value: string) => void
  onInstructionsChange: (value: string) => void
  onModelChange: (value: string) => void
  onVoiceChange?: (value: string) => void
  onTemperatureChange: (value: number | undefined) => void
  onTimeoutSecondsChange: (value: number | undefined) => void
}

export const GeminiRealtimeFields: React.FC<GeminiRealtimeFieldsProps> = ({
  apiKey,
  instructions,
  model,
  voice,
  temperature,
  timeoutSeconds,
  onApiKeyChange,
  onInstructionsChange,
  onModelChange,
  onVoiceChange,
  onTemperatureChange,
  onTimeoutSecondsChange,
  disabled,
  className = "",
}) => {
  const [isApiKeyVisible, setIsApiKeyVisible] = useState(false)

  // Google issues keys in multiple formats: AIzaSy… (AI Studio) and AQ.…
  // (Express Mode). We don't validate on prefix — just require non-empty.
  const timeoutError = getTimeoutSecondsError(timeoutSeconds)

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Google AI API Key</Label>
        <Input
          type={isApiKeyVisible ? "text" : "password"}
          autoComplete="new-password"
          data-lpignore="true"
          data-1p-ignore
          placeholder="AIzaSy… or AQ.Ab…"
          value={apiKey}
          onChange={(e) => onApiKeyChange(e.target.value)}
          className={className}
          disabled={disabled}
          required
          endAdornment={
            <button
              type="button"
              onClick={() => setIsApiKeyVisible(!isApiKeyVisible)}
              aria-label={isApiKeyVisible ? "Hide API key" : "Show API key"}
            >
              {isApiKeyVisible ? (
                <EyeOff className="w-4 h-4" />
              ) : (
                <Eye className="w-4 h-4" />
              )}
            </button>
          }
        />
      </div>
      <div className="space-y-2">
        <Label>Agent System Instructions (Optional)</Label>
        <Textarea
          placeholder="You are a friendly customer service agent for Acme Corp. Be concise and helpful. Always confirm understanding before taking actions."
          value={instructions || ""}
          onChange={(e) => onInstructionsChange(e.target.value)}
          className={`text-sm ${className}`}
          disabled={disabled}
          rows={4}
          aria-label="Agent System Instructions"
        />
      </div>
      <div className={onVoiceChange ? "grid grid-cols-2 gap-4" : ""}>
        <div className="space-y-2">
          <Label>Model</Label>
          <Select
            value={model || GEMINI_REALTIME_DEFAULT_MODEL}
            onValueChange={(value) => {
              if (value) {
                onModelChange(value)
              }
            }}
            disabled={disabled}
          >
            <SelectTrigger aria-label="Select Gemini Live Model">
              <SelectValue placeholder="Select a model" />
            </SelectTrigger>
            <SelectContent>
              {GEMINI_REALTIME_MODELS.map((modelOption) => (
                <SelectItem key={modelOption.value} value={modelOption.value}>
                  {modelOption.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {onVoiceChange && (
          <div className="space-y-2">
            <Label>Agent Voice</Label>
            <Select
              value={voice || GEMINI_REALTIME_DEFAULT_VOICE}
              onValueChange={(value) => {
                if (value) {
                  onVoiceChange(value)
                }
              }}
              disabled={disabled}
            >
              <SelectTrigger aria-label="Select Gemini Live Voice">
                <SelectValue placeholder="Select a voice" />
              </SelectTrigger>
              <SelectContent>
                {GEMINI_REALTIME_VOICES.map((voiceOption) => (
                  <SelectItem
                    key={voiceOption.value}
                    value={voiceOption.value}
                    description={voiceOption.description}
                  >
                    {voiceOption.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <div className="flex items-center justify-between mb-1">
            <Label>Temperature (Optional)</Label>
            <span className="text-sm font-medium text-coval-text">
              {temperature !== undefined ? temperature.toFixed(1) : "0.8"}
            </span>
          </div>
          <Slider
            aria-label="Temperature"
            step={0.1}
            min={0}
            max={2}
            value={[temperature !== undefined ? temperature : 0.8]}
            onValueChange={(values) => {
              onTemperatureChange(values[0])
            }}
            className={className}
            disabled={disabled}
          />
        </div>
        <div className="space-y-2">
          <Label>Timeout (Optional)</Label>
          <Input
            type="number"
            placeholder="900"
            min={1}
            max={1800}
            value={timeoutSeconds?.toString() || ""}
            onChange={(e) => {
              // Preserve out-of-range values so the inline `timeoutError`
              // stays visible while the user corrects them. Only collapse
              // to `undefined` when the field is cleared or non-numeric.
              onTimeoutSecondsChange(parseTimeoutSecondsInput(e.target.value))
            }}
            className={className}
            disabled={disabled}
            error={!!timeoutError}
          />
          {timeoutError && (
            <p className="text-xs text-coval-danger-text">{timeoutError}</p>
          )}
        </div>
      </div>
    </div>
  )
}

// NOTE: no temperature prop — the Grok Voice Agent API does not expose a
// temperature knob in its session config.
interface GrokRealtimeFieldsProps extends BaseFormFieldProps {
  apiKey: string
  instructions?: string
  model?: string
  voice?: string
  timeoutSeconds?: number
  onApiKeyChange: (value: string) => void
  onInstructionsChange: (value: string) => void
  onModelChange: (value: string) => void
  onVoiceChange?: (value: string) => void
  onTimeoutSecondsChange: (value: number | undefined) => void
}

export const GrokRealtimeFields: React.FC<GrokRealtimeFieldsProps> = ({
  apiKey,
  instructions,
  model,
  voice,
  timeoutSeconds,
  onApiKeyChange,
  onInstructionsChange,
  onModelChange,
  onVoiceChange,
  onTimeoutSecondsChange,
  disabled,
  className = "",
}) => {
  const [isApiKeyVisible, setIsApiKeyVisible] = useState(false)

  const apiKeyError = getApiKeyPrefixError(apiKey, "xai-")
  const timeoutError = getTimeoutSecondsError(timeoutSeconds)

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>xAI API Key</Label>
        <Input
          type={isApiKeyVisible ? "text" : "password"}
          autoComplete="new-password"
          data-lpignore="true"
          data-1p-ignore
          placeholder="xai-..."
          value={apiKey}
          onChange={(e) => onApiKeyChange(e.target.value)}
          className={className}
          disabled={disabled}
          required
          error={!!apiKeyError}
          endAdornment={
            <button
              type="button"
              onClick={() => setIsApiKeyVisible(!isApiKeyVisible)}
              aria-label={isApiKeyVisible ? "Hide API key" : "Show API key"}
            >
              {isApiKeyVisible ? (
                <EyeOff className="w-4 h-4" />
              ) : (
                <Eye className="w-4 h-4" />
              )}
            </button>
          }
        />
        {apiKeyError && (
          <p className="text-xs text-coval-danger-text">{apiKeyError}</p>
        )}
      </div>
      <div className="space-y-2">
        <Label>Agent System Instructions (Optional)</Label>
        <Textarea
          placeholder="You are a friendly customer service agent for Acme Corp. Be concise and helpful. Always confirm understanding before taking actions."
          value={instructions || ""}
          onChange={(e) => onInstructionsChange(e.target.value)}
          className={`text-sm ${className}`}
          disabled={disabled}
          rows={4}
          aria-label="Agent System Instructions"
        />
      </div>
      <div className={onVoiceChange ? "grid grid-cols-2 gap-4" : ""}>
        <div className="space-y-2">
          <Label>Model</Label>
          <Select
            value={model || GROK_REALTIME_DEFAULT_MODEL}
            onValueChange={(value) => {
              if (value) {
                onModelChange(value)
              }
            }}
            disabled={disabled}
          >
            <SelectTrigger aria-label="Select Grok Realtime Model">
              <SelectValue placeholder="Select a model" />
            </SelectTrigger>
            <SelectContent>
              {GROK_REALTIME_MODELS.map((modelOption) => (
                <SelectItem key={modelOption.value} value={modelOption.value}>
                  {modelOption.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {onVoiceChange && (
          <div className="space-y-2">
            <Label>Agent Voice</Label>
            <Select
              value={voice || GROK_REALTIME_DEFAULT_VOICE}
              onValueChange={(value) => {
                if (value) {
                  onVoiceChange(value)
                }
              }}
              disabled={disabled}
            >
              <SelectTrigger aria-label="Select Grok Realtime Voice">
                <SelectValue placeholder="Select a voice" />
              </SelectTrigger>
              <SelectContent>
                {GROK_REALTIME_VOICES.map((voiceOption) => (
                  <SelectItem
                    key={voiceOption.value}
                    value={voiceOption.value}
                    description={voiceOption.description}
                  >
                    {voiceOption.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Timeout (Optional)</Label>
          <Input
            type="number"
            placeholder="900"
            min={1}
            max={1800}
            value={timeoutSeconds?.toString() || ""}
            onChange={(e) => {
              // Preserve out-of-range values so the inline `timeoutError`
              // stays visible while the user corrects them. Only collapse
              // to `undefined` when the field is cleared or non-numeric.
              onTimeoutSecondsChange(parseTimeoutSecondsInput(e.target.value))
            }}
            className={className}
            disabled={disabled}
            error={!!timeoutError}
          />
          {timeoutError && (
            <p className="text-xs text-coval-danger-text">{timeoutError}</p>
          )}
        </div>
      </div>
    </div>
  )
}

interface OpenAIRealtimeFieldsProps extends BaseFormFieldProps {
  apiKey: string
  apiKeySource: OpenAIRealtimeApiKeySource
  instructions?: string
  model?: string
  voice?: string
  temperature?: number
  timeoutSeconds?: number
  webSearchEnabled?: boolean
  onApiKeyChange: (value: string) => void
  onApiKeySourceChange: (value: OpenAIRealtimeApiKeySource) => void
  onInstructionsChange: (value: string) => void
  onModelChange: (value: string, disableWebSearch?: boolean) => void
  onVoiceChange?: (value: string) => void
  onTemperatureChange: (value: number | undefined) => void
  onTimeoutSecondsChange: (value: number | undefined) => void
  onWebSearchEnabledChange?: (value: boolean) => void
  showGptLiveAlpha?: boolean
}

export const OpenAIRealtimeFields: React.FC<OpenAIRealtimeFieldsProps> = ({
  apiKey,
  apiKeySource,
  instructions,
  model,
  voice,
  temperature,
  timeoutSeconds,
  webSearchEnabled,
  onApiKeyChange,
  onApiKeySourceChange,
  onInstructionsChange,
  onModelChange,
  onVoiceChange,
  onTemperatureChange,
  onTimeoutSecondsChange,
  onWebSearchEnabledChange,
  showGptLiveAlpha = false,
  disabled,
  className = "",
}) => {
  const [isApiKeyVisible, setIsApiKeyVisible] = useState(false)

  const apiKeyError =
    apiKeySource === OPENAI_REALTIME_API_KEY_SOURCE.CUSTOMER_PROVIDED
      ? getApiKeyPrefixError(apiKey, "sk-")
      : null
  const timeoutError = getTimeoutSecondsError(timeoutSeconds)
  const webSearchCheckboxId = useId()
  const covalManagedApiKeyId = useId()
  const customerProvidedApiKeyId = useId()
  const modelOptions = showGptLiveAlpha
    ? [...OPENAI_REALTIME_MODELS, ...OPENAI_GPT_LIVE_ALPHA_MODELS]
    : OPENAI_REALTIME_MODELS
  const isGptLiveAlphaSelected = OPENAI_GPT_LIVE_ALPHA_MODELS.some(
    (modelOption) => modelOption.value === model,
  )
  const isGptLiveSelected = model === "gpt-live-1"
  // Marin is available for both public Live and private alpha sessions.
  const voiceOptions = OPENAI_REALTIME_VOICES.filter(
    (voiceOption) =>
      isGptLiveSelected ||
      isGptLiveAlphaSelected ||
      !OPENAI_GPT_LIVE_ALPHA_ONLY_VOICE_VALUES.has(voiceOption.value),
  )

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>API Key Source</Label>
        <RadioGroup
          value={apiKeySource}
          onValueChange={(value) => {
            if (isOpenAIRealtimeApiKeySource(value)) {
              setIsApiKeyVisible(false)
              onApiKeySourceChange(value)
            }
          }}
          disabled={disabled}
          className="gap-3"
        >
          <div className="flex items-start gap-2">
            <RadioGroupItem
              id={covalManagedApiKeyId}
              value={OPENAI_REALTIME_API_KEY_SOURCE.COVAL_MANAGED}
            />
            <div>
              <Label htmlFor={covalManagedApiKeyId}>
                Coval provided API key
              </Label>
              <p className="text-xs text-coval-text-muted">
                Coval manages the OpenAI credential used for simulations.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <RadioGroupItem
              id={customerProvidedApiKeyId}
              value={OPENAI_REALTIME_API_KEY_SOURCE.CUSTOMER_PROVIDED}
            />
            <Label htmlFor={customerProvidedApiKeyId}>My API key</Label>
          </div>
        </RadioGroup>
      </div>
      {apiKeySource === OPENAI_REALTIME_API_KEY_SOURCE.CUSTOMER_PROVIDED && (
        <div className="space-y-2">
          <Label>OpenAI API Key</Label>
          <Input
            type={isApiKeyVisible ? "text" : "password"}
            autoComplete="new-password"
            data-lpignore="true"
            data-1p-ignore
            placeholder="sk-proj-..."
            value={apiKey}
            onChange={(e) => onApiKeyChange(e.target.value)}
            className={className}
            disabled={disabled}
            required
            error={!!apiKeyError}
            endAdornment={
              <button
                type="button"
                onClick={() => setIsApiKeyVisible(!isApiKeyVisible)}
                aria-label={isApiKeyVisible ? "Hide API key" : "Show API key"}
              >
                {isApiKeyVisible ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
              </button>
            }
          />
          {apiKeyError && (
            <p className="text-xs text-coval-danger-text">{apiKeyError}</p>
          )}
        </div>
      )}
      <div className="space-y-2">
        <Label>Agent System Instructions (Optional)</Label>
        <Textarea
          placeholder="You are a friendly customer service agent for Acme Corp. Be concise and helpful. Always confirm understanding before taking actions."
          value={instructions || ""}
          onChange={(e) => onInstructionsChange(e.target.value)}
          className={`text-sm ${className}`}
          disabled={disabled}
          rows={4}
          aria-label="Agent System Instructions"
        />
      </div>
      <div className={onVoiceChange ? "grid grid-cols-2 gap-4" : ""}>
        <div className="space-y-2">
          <Label>Model</Label>
          <Select
            value={model || OPENAI_REALTIME_DEFAULT_MODEL}
            onValueChange={(value) => {
              if (value) {
                const isNewModelLiveAlpha = OPENAI_GPT_LIVE_ALPHA_MODELS.some(
                  (modelOption) => modelOption.value === value,
                )
                const disableWebSearch =
                  isNewModelLiveAlpha && webSearchEnabled === true
                onModelChange(value, disableWebSearch)
              }
            }}
            disabled={disabled}
          >
            <SelectTrigger aria-label="Select OpenAI Realtime Model">
              <SelectValue placeholder="Select a model" />
            </SelectTrigger>
            <SelectContent>
              {modelOptions.map((modelOption) => (
                <SelectItem key={modelOption.value} value={modelOption.value}>
                  {modelOption.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {onVoiceChange && (
          <div className="space-y-2">
            <Label>Agent Voice</Label>
            <Select
              value={voice || OPENAI_REALTIME_DEFAULT_VOICE}
              onValueChange={(value) => {
                if (value) {
                  onVoiceChange(value)
                }
              }}
              disabled={disabled}
            >
              <SelectTrigger aria-label="Select OpenAI Realtime Voice">
                <SelectValue placeholder="Select a voice" />
              </SelectTrigger>
              <SelectContent>
                {voiceOptions.map((voiceOption) => (
                  <SelectItem
                    key={voiceOption.value}
                    value={voiceOption.value}
                    description={voiceOption.description}
                  >
                    {voiceOption.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <div className="flex items-center justify-between mb-1">
            <Label>Temperature (Optional)</Label>
            <span className="text-sm font-medium text-coval-text">
              {temperature !== undefined ? temperature.toFixed(1) : "0.8"}
            </span>
          </div>
          <Slider
            aria-label="Temperature"
            step={0.1}
            min={0}
            max={2}
            value={[temperature !== undefined ? temperature : 0.8]}
            onValueChange={(values) => {
              onTemperatureChange(values[0])
            }}
            className={className}
            disabled={disabled}
          />
        </div>
        <div className="space-y-2">
          <Label>Timeout (Optional)</Label>
          <Input
            type="number"
            placeholder="900"
            min={1}
            max={1800}
            value={timeoutSeconds?.toString() || ""}
            onChange={(e) => {
              // Preserve out-of-range values so the inline `timeoutError`
              // stays visible while the user corrects them. Only collapse
              // to `undefined` when the field is cleared or non-numeric.
              onTimeoutSecondsChange(parseTimeoutSecondsInput(e.target.value))
            }}
            className={className}
            disabled={disabled}
            error={!!timeoutError}
          />
          {timeoutError && (
            <p className="text-xs text-coval-danger-text">{timeoutError}</p>
          )}
        </div>
      </div>
      {onWebSearchEnabledChange && !isGptLiveAlphaSelected && (
        <Accordion type="single" collapsible className="px-0">
          <AccordionItem value="openai-realtime-advanced">
            <AccordionTrigger>
              <div className="flex flex-col items-start gap-0.5 text-left">
                <span>Advanced Settings</span>
                <span className="text-xs text-coval-text-muted font-normal">
                  Optional capabilities for your OpenAI Realtime agent
                </span>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <div className="space-y-4 pt-2">
                <div className="flex items-start gap-2">
                  <Checkbox
                    id={webSearchCheckboxId}
                    checked={webSearchEnabled ?? false}
                    onCheckedChange={(checked) =>
                      onWebSearchEnabledChange(checked === true)
                    }
                    disabled={disabled}
                    className="mt-0.5"
                  />
                  <div className="space-y-1">
                    <label
                      htmlFor={webSearchCheckboxId}
                      className="text-sm font-medium text-coval-text"
                    >
                      Web Search
                    </label>
                    <p className="text-xs text-coval-text-muted">
                      Allow the agent to search the web for up-to-date
                      information during the conversation.
                    </p>
                  </div>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      )}
    </div>
  )
}

export { DeepgramRealtimeFields } from "./DeepgramRealtimeFormFields"
